import { useReducedMotion } from "framer-motion";

import type { PolymorphicProps, ThemeUiProps } from "@theme/types/global";
import type { ElementType } from "react";

interface BaseProps extends ThemeUiProps {
  label: string;
  active?: boolean;
  /** If this is navigation (e.g., goes to a page), use aria-current; else use aria-selected */
  isNavigation?: boolean;
}

export type DropdownMenuItemProps<C extends ElementType> = PolymorphicProps<C, BaseProps>;

export const DropdownMenuItem = <C extends ElementType = "button">({
  as,
  label,
  active,
  isNavigation = false,
  sx,
  ...props
}: DropdownMenuItemProps<C>) => {
  const reducedMotion = useReducedMotion();

  const Component = (as || "button") as ElementType;
  const ariaProps = isNavigation
    ? ({ "aria-current": active ? "page" : undefined } as const)
    : ({ "aria-selected": active ? "true" : "false" } as const);

  return (
    <Component
      role="menuitem"
      {...ariaProps}
      {...props}
      data-active={active ? "true" : "false"}
      sx={{
        variant: "text.sansBody01Regular",
        position: "relative",
        px: "s200",
        my: "s200",
        mx: "s300",
        whiteSpace: "nowrap",
        textDecoration: "none",
        width: "fit-content",
        fontWeight: "regular",
        border: "none",
        background: "none",
        textAlign: "left",
        borderRadius: "xs",
        cursor: "pointer",
        color: "textPrimary",

        "&:hover:before": {
          backgroundColor: "grey300",
        },

        "&:focus-visible": {
          outlineColor: "borderBrand",
          outlineWidth: "thick",
          outlineOffset: "4px",
        },

        "&:before": {
          content: "''",
          position: "absolute",
          height: "100%",
          width: 1,
          left: 0,
          top: 0,
          backgroundColor: "transparent",
          transition: reducedMotion ? "none" : "background-color 0.2s ease-in-out",
        },

        '&[data-active="true"]': {
          fontWeight: "medium",

          "&:before": {
            backgroundColor: "primary",
          },
        },
        ...sx,
      }}
    >
      {label}
    </Component>
  );
};
